import React from "react";
import { RecipeMacroTotals } from "../../utils/recipeMacros";
import {
  CaloriesIcon,
  ProteinIcon,
  CarbsIcon,
  FatIcon,
  FiberIcon,
  SodiumIcon,
} from "../icons/NutritionIcons";

export type NutrientKey = keyof RecipeMacroTotals;

export interface NutrientMeta {
  key: NutrientKey;
  label: string;
  unit: string;
  decimals: number;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  box: string;
  accent: string;
}

// Single source of truth for nutrient display — colors, icons, labels, and
// units — shared by the recipe detail page tiles and the modal nutrition tiles.
export const NUTRIENTS: readonly NutrientMeta[] = [
  {
    key: "calories",
    label: "Calories",
    unit: "kcal",
    decimals: 0,
    Icon: CaloriesIcon,
    box: "border border-biafit-primary bg-white dark:bg-navy-800",
    accent: "text-biafit-primary",
  },
  {
    key: "protein",
    label: "Protein",
    unit: "g",
    decimals: 1,
    Icon: ProteinIcon,
    box: "bg-[#CDDCFF] dark:bg-[#245BDC]/20",
    accent: "text-[#245BDC] dark:text-[#CDDCFF]",
  },
  {
    key: "carbs",
    label: "Carbs",
    unit: "g",
    decimals: 1,
    Icon: CarbsIcon,
    box: "bg-[#FFD8E6] dark:bg-[#D1366F]/20",
    accent: "text-[#D1366F] dark:text-[#FFD8E6]",
  },
  {
    key: "fat",
    label: "Fat",
    unit: "g",
    decimals: 1,
    Icon: FatIcon,
    box: "bg-[#FFE5C5] dark:bg-[#F3860A]/20",
    accent: "text-[#F3860A] dark:text-[#FFE5C5]",
  },
  {
    key: "fiber",
    label: "Fiber",
    unit: "g",
    decimals: 1,
    Icon: FiberIcon,
    box: "bg-[#C5FFDA] dark:bg-[#1EA851]/20",
    accent: "text-[#1EA851] dark:text-[#C5FFDA]",
  },
  {
    key: "sodium_mg",
    label: "Sodium",
    unit: "mg",
    decimals: 0,
    Icon: SodiumIcon,
    box: "bg-[#F3D3FF] dark:bg-[#B740E2]/20",
    accent: "text-[#B740E2] dark:text-[#F3D3FF]",
  },
];

// Hidden from the UI; remove a key here to bring it back.
const HIDDEN_NUTRIENT_KEYS: ReadonlySet<NutrientKey> = new Set([
  "fiber",
  "sodium_mg",
]);

// Nutrients shown in the modal and detail page.
export const VISIBLE_NUTRIENTS: readonly NutrientMeta[] = NUTRIENTS.filter(
  (meta) => !HIDDEN_NUTRIENT_KEYS.has(meta.key)
);

export const formatMacroValue = (value: number, decimals: number): string =>
  value.toLocaleString("en-US", { maximumFractionDigits: decimals });

const DECIMALS_BY_KEY = Object.fromEntries(
  NUTRIENTS.map((meta) => [meta.key, meta.decimals])
) as Record<NutrientKey, number>;

export const formatNutrientAmount = (key: NutrientKey, value: number): string =>
  formatMacroValue(value, DECIMALS_BY_KEY[key]);

interface NutrientTileProps {
  meta: NutrientMeta;
  className?: string;
  iconClassName?: string;
  children: React.ReactNode;
}

export const NutrientTile: React.FC<NutrientTileProps> = ({
  meta,
  className = "p-3",
  iconClassName = "mx-auto mb-1 h-6 w-6",
  children,
}) => {
  const { Icon, box, accent } = meta;
  return (
    <div className={`rounded-lg text-center ${box} ${className}`}>
      <Icon className={`${iconClassName} ${accent}`} />
      {children}
    </div>
  );
};
